/** Contrato vinculado a uma proposta de adequações e reformas. */
export type ContratoAdequacaoResumo = {
  id: string;
  proposta_id: string;
  contrato_pdf_url?: string | null;
  status?: string | null;
  created_at?: string | null;
};

/**
 * Escolhe o contrato que deve representar a proposta na UI:
 * prioriza o mais recente que já tem PDF; senão, o registro mais recente.
 */
export function getContratoPrincipalAdequacao(
  contratos?: ContratoAdequacaoResumo[] | null
): ContratoAdequacaoResumo | undefined {
  if (!contratos?.length) return undefined;

  const ordenados = [...contratos].sort((a, b) => {
    const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
    const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
    return tb - ta;
  });

  return ordenados.find((c) => Boolean(c.contrato_pdf_url)) ?? ordenados[0];
}

/** Anexa à proposta apenas o contrato principal (evita rascunhos antigos na posição [0]). */
export function anexarContratoPrincipalAdequacao<T extends { id: string }>(
  proposta: T,
  contratos: ContratoAdequacaoResumo[]
): T & { contratos_adequacoes_reformas: ContratoAdequacaoResumo[] } {
  const daProposta = contratos.filter((c) => c.proposta_id === proposta.id);
  const principal = getContratoPrincipalAdequacao(daProposta);
  return {
    ...proposta,
    contratos_adequacoes_reformas: principal ? [principal] : [],
  };
}

/** Monta texto de escopo a partir da proposta aceita (para o PDF do contrato). */
export function montarEscopoResumoProposta(proposta: {
  descricao_servico?: string | null;
  pdf_conteudo?: unknown;
}): string {
  const extra =
    proposta.pdf_conteudo && typeof proposta.pdf_conteudo === "object"
      ? (proposta.pdf_conteudo as Record<string, unknown>)
      : null;

  const linhas = Array.isArray(extra?.investimento_linhas)
    ? (extra!.investimento_linhas as Array<{ descricao?: string; quantidade?: number; total?: number }>)
    : [];

  const itens = linhas
    .map((l) => String(l?.descricao || "").trim())
    .filter((d) => d.length > 0 && !/^desconto/i.test(d));

  if (itens.length) {
    return itens.map((d, i) => `${i + 1}. ${d}`).join("\n");
  }

  const detalhamento =
    typeof extra?.detalhamento_escopo === "string" ? extra.detalhamento_escopo.trim() : "";
  if (detalhamento) return detalhamento;

  return (proposta.descricao_servico || "").trim();
}
